import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileSignature } from 'lucide-react';
import api from '../services/api';

export default function ListaAlunos() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Pegando todos os dados que mandamos da Home
  const { turmaId, nomeTurma, avaliacaoId, qtdQuestoes, qtdAlternativas } = location.state || {};
  
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    carregarAlunos();
  }, [turmaId, avaliacaoId]);

  const carregarResultados = async () => {
    if (!avaliacaoId) return [];

    try {
      // ATENÇÃO: Verifique o prefixo (ex: /resultados-objetiva, /resultados) configurado no seu backend
      const response = await api.get(`/resultadosobjetivas/avaliacao/${avaliacaoId}`);
      
      // O backend retorna um array de resultados. Pegamos apenas os IDs dos alunos.
      return response.data.map(resultado => resultado.aluno_id);
    } catch (error) {
      console.error("Erro ao buscar resultados:", error);
      return [];
    }
  };

  const carregarAlunos = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/alunos/${turmaId}`);
      
      // Blindagem para garantir que seja um array
      const dados = Array.isArray(response.data) 
        ? response.data 
        : (response.data ? [response.data] : []);

      // Busca a lista de IDs de quem já fez essa prova
      const alunosComResultadoIds = await carregarResultados();

      // Cruza os dados: se o ID do aluno estiver na lista de resultados, o status muda
      const alunosAtualizados = dados.map(aluno => ({
        ...aluno,
        status: alunosComResultadoIds.includes(aluno.id) ? "Avaliado" : "Pendente"
      }));

      setAlunos(alunosAtualizados);

    } catch (error) {
      console.error("Erro ao carregar alunos:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <p>Carregando alunos...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>Voltar</button>
        <div>
          <h2 style={styles.title}>Alunos da Turma</h2>
          <p style={styles.subtitle}>{nomeTurma || "Carregando..."}</p>
        </div>
      </header>

      <div style={styles.listContent}>
        {alunos.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', marginTop: '20px' }}>
            Nenhum aluno encontrado para esta turma.
          </p>
        ) : (
          alunos.map(item => {
            const isExpanded = expandedId === item.id;
            const isAvaliado = item.status.toLowerCase() === 'avaliado';

            return (
              <div key={item.id} style={{...styles.card, borderColor: isExpanded ? '#5d9e03' : '#eee'}}>
                <div style={styles.cardHeader} onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                  <div style={styles.avatar}>{item.nome?.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <h4 style={styles.name}>{item.nome}</h4>
                    <div style={styles.statusRow}>
                      <div style={{...styles.statusDot, backgroundColor: isAvaliado ? '#4caf50' : '#ff9800'}} />
                      <span style={{ fontSize: '12px', color: isAvaliado ? '#4caf50' : '#ff9800', fontWeight: 'bold' }}>
                        {item.status}
                      </span>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={20} color="#ccc"/> : <ChevronDown size={20} color="#ccc"/>}
                </div>

                {isExpanded && (
                  <div style={styles.expandedContent}>
                    <hr style={styles.divider} />
                    
                    {isAvaliado ? (
                      <p style={styles.infoText}>✅ Avaliação já registrada para este aluno.</p>
                    ) : (
                      <button 
                        style={styles.mainButton} 
                        onClick={() => navigate('/gabarito', {
                          state: {
                            aluno: item,
                            avaliacaoId: avaliacaoId,
                            qtdQuestoes: qtdQuestoes,
                            qtdAlternativas: qtdAlternativas
                          }
                        })}
                      >
                        {/* Troquei o ícone do microfone pelo de formulário (FileSignature) */}
                        <FileSignature size={18} style={{ marginRight: '8px' }} />
                        Preencher Gabarito
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: '600px', margin: '0 auto', backgroundColor: '#F8F9FA', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', padding: '20px', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  backBtn: { marginRight: '15px', background: 'none', border: 'none', color: '#5d9e03', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' },
  title: { margin: 0, fontSize: '20px', color: '#333' },
  subtitle: { margin: 0, color: '#5d9e03', fontSize: '14px' },
  listContent: { padding: '20px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', marginBottom: '12px', border: '1px solid #eee', cursor: 'pointer', overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'center', padding: '15px' },
  avatar: { width: '45px', height: '45px', borderRadius: '12px', background: 'linear-gradient(to bottom, #84c308, #5d9e03)', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '18px', fontWeight: 'bold', marginRight: '15px' },
  name: { margin: 0, fontSize: '16px', color: '#2c3e50' },
  statusRow: { display: 'flex', alignItems: 'center', marginTop: '4px' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', marginRight: '6px' },
  expandedContent: { padding: '15px', backgroundColor: '#fafafa' },
  divider: { borderTop: '1px solid #eee', margin: '0 0 15px 0' },
  infoText: { margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '500' },
  mainButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '12px', background: 'linear-gradient(to bottom, #3f8c00, #2d6500)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }
};